/**
 * DOG OWNED — the local pack draft.
 *
 * One dog's in-progress pack on this device: which areas they chose, what they
 * put in each, and one canonical item per variant no matter how many areas point
 * at it. No backend, one key, cleared independently of the dog profile.
 *
 * It is a draft, never an order. It stores variant and product IDs, quantities,
 * area membership and a sanitised snapshot of the dog — and deliberately not
 * prices, product titles, raw product objects, health notes, email or account
 * IDs. Prices and availability are always read fresh from Shopify; the draft is
 * never the authority for them.
 *
 * The one structural rule that everything else serves: a variant is canonical.
 * Selecting the same variant from two areas does not duplicate it — it merges
 * the area membership. That is what makes "add my pack to basket" honest: one
 * line per variant, never two because it happened to help two areas.
 *
 * Two journeys share this one engine through `createStore`: Build My Dog's Pack
 * (the default `dog_owned_pack_v1` store, still `window.PackDraft`) and Puppy's
 * First Pack (`dog_owned_puppy_pack_v1`). Same validator, same canonical model,
 * separate keys — one never overwrites the other, and no second parser exists.
 */
window.PackDraft = (function () {
  'use strict';

  var VERSION = 1;
  var MAX_QTY = 10;
  var STATUSES = ['not_started', 'selected', 'already_owned', 'skipped', 'saved_for_later'];
  var DECISIVE = ['already_owned', 'skipped', 'saved_for_later'];

  /* ---- pure helpers (independent of which store they serve) ---- */

  function isGid(v) { return typeof v === 'string' && v.indexOf('gid://shopify/') === 0; }
  function clampQty(q) {
    q = parseInt(q, 10);
    if (!q || q < 1) return 1;
    return q > MAX_QTY ? MAX_QTY : q;
  }
  function cleanStr(v, max) {
    if (typeof v !== 'string') return '';
    return v.length <= (max || 40) ? v : '';
  }
  function cleanList(v) {
    if (!Array.isArray(v)) return [];
    return v.filter(function (x) { return typeof x === 'string' && x.length > 0 && x.length < 64; });
  }
  function cleanSnapshot(s) {
    s = s || {};
    return {
      name: typeof s.name === 'string' ? s.name.slice(0, 32) : '',
      size: cleanStr(s.size, 16),
      life_stage: cleanStr(s.life_stage, 16),
      characteristics: cleanList(s.characteristics),
      // Optional context (Puppy's First Pack "buying as a gift"). The everyday
      // store never sets it; matchesProfile ignores it, so it affects no match.
      gift: !!s.gift
    };
  }
  function uniqPush(list, value) { if (list.indexOf(value) === -1) list.push(value); }

  /**
   * A key-scoped draft store. `window.PackDraft` is the default one; a second
   * journey makes its own with `createStore({ key, journey, allowedAreas })`.
   * Every function here closes over exactly one storage key, so two stores never
   * touch each other's data.
   *
   * @param {object} [opts]
   * @param {string} [opts.key='dog_owned_pack_v1'] - the localStorage key.
   * @param {string} [opts.journey] - a label stamped on the draft (e.g. 'puppys_first_pack').
   * @param {string[]} [opts.allowedAreas] - the journey's areas, exposed for callers.
   */
  function createStore(opts) {
    opts = opts || {};
    var KEY = typeof opts.key === 'string' && opts.key ? opts.key : 'dog_owned_pack_v1';
    var JOURNEY = typeof opts.journey === 'string' ? opts.journey : '';
    var ALLOWED = Array.isArray(opts.allowedAreas) ? opts.allowedAreas.slice() : null;

    function safeGet() { try { return window.localStorage.getItem(KEY); } catch (e) { return null; } }
    function safeSet(v) { try { window.localStorage.setItem(KEY, v); return true; } catch (e) { return false; } }
    function safeRemove() { try { window.localStorage.removeItem(KEY); } catch (e) { /* non-fatal */ } }

    /**
     * Read and validate. Anything that is not a well-formed version-1 draft is
     * removed and treated as absent — a wrong guess about old data is worse than
     * a clean start, and the customer only loses selections they can re-make.
     * An older draft with no `journey` field reads fine (it defaults to '') — no
     * migration is needed.
     */
    function read() {
      var raw = safeGet();
      if (!raw) return null;
      var d;
      try { d = JSON.parse(raw); } catch (e) { safeRemove(); return null; }
      if (!d || typeof d !== 'object' || Array.isArray(d) || d.version !== VERSION) { safeRemove(); return null; }

      var items = {};
      if (d.items && typeof d.items === 'object') {
        Object.keys(d.items).forEach(function (vid) {
          var it = d.items[vid];
          if (!it || !isGid(vid) || !isGid(it.variant_id) || !isGid(it.product_id)) return;
          items[vid] = {
            product_id: it.product_id,
            variant_id: it.variant_id,
            handle: cleanStr(it.handle, 120),
            quantity: clampQty(it.quantity),
            area_keys: cleanList(it.area_keys)
          };
        });
      }

      var areas = {};
      if (d.areas && typeof d.areas === 'object') {
        Object.keys(d.areas).forEach(function (k) {
          var a = d.areas[k];
          if (!a) return;
          var status = STATUSES.indexOf(a.status) !== -1 ? a.status : 'not_started';
          var vids = cleanList(a.variant_ids).filter(function (v) { return items[v]; });
          areas[k] = { status: status, variant_ids: vids };
        });
      }

      return {
        version: VERSION,
        journey: typeof d.journey === 'string' ? d.journey : '',
        profile_snapshot: cleanSnapshot(d.profile_snapshot),
        selected_areas: cleanList(d.selected_areas),
        areas: areas,
        items: items,
        created_at: typeof d.created_at === 'string' ? d.created_at : '',
        updated_at: typeof d.updated_at === 'string' ? d.updated_at : ''
      };
    }

    function create(snapshot, selectedAreas) {
      var now = new Date().toISOString();
      var areas = {};
      cleanList(selectedAreas).forEach(function (k) { areas[k] = { status: 'not_started', variant_ids: [] }; });
      return {
        version: VERSION,
        journey: JOURNEY,
        profile_snapshot: cleanSnapshot(snapshot),
        selected_areas: cleanList(selectedAreas),
        areas: areas,
        items: {},
        created_at: now,
        updated_at: now
      };
    }

    function write(draft) {
      if (!draft) return null;
      draft.version = VERSION;
      if (!draft.journey) draft.journey = JOURNEY;
      draft.updated_at = new Date().toISOString();
      if (!draft.created_at) draft.created_at = draft.updated_at;
      return safeSet(JSON.stringify(draft)) ? draft : null;
    }

    function forget() { safeRemove(); }

    /* ---- mutations (return the same draft for chaining; caller persists) ---- */

    function ensureArea(draft, areaKey) {
      if (!draft.areas[areaKey]) draft.areas[areaKey] = { status: 'not_started', variant_ids: [] };
      if (draft.selected_areas.indexOf(areaKey) === -1) draft.selected_areas.push(areaKey);
      return draft.areas[areaKey];
    }

    /**
     * Add a variant to an area. Canonical: if the variant is already in the pack
     * (from any area), only its area membership grows — it is never duplicated.
     */
    function addItem(draft, addOpts) {
      var vid = addOpts.variantId, pid = addOpts.productId, areaKey = addOpts.areaKey;
      if (!isGid(vid) || !isGid(pid) || !areaKey) return draft;

      var item = draft.items[vid];
      if (!item) {
        item = { product_id: pid, variant_id: vid, handle: addOpts.handle || '',
          quantity: clampQty(addOpts.quantity || 1), area_keys: [] };
        draft.items[vid] = item;
      } else if (addOpts.quantity) {
        item.quantity = clampQty(addOpts.quantity);
      }
      uniqPush(item.area_keys, areaKey);

      var area = ensureArea(draft, areaKey);
      uniqPush(area.variant_ids, vid);
      area.status = 'selected';
      return draft;
    }

    /**
     * Remove a variant from one area. The canonical item survives while any other
     * area still points at it; it is deleted only when the last area lets it go.
     */
    function removeItem(draft, areaKey, vid) {
      var area = draft.areas[areaKey];
      if (area) {
        area.variant_ids = area.variant_ids.filter(function (v) { return v !== vid; });
        if (!area.variant_ids.length && area.status === 'selected') area.status = 'not_started';
      }
      var item = draft.items[vid];
      if (item) {
        item.area_keys = item.area_keys.filter(function (k) { return k !== areaKey; });
        if (!item.area_keys.length) delete draft.items[vid];
      }
      return draft;
    }

    function setStatus(draft, areaKey, status) {
      if (STATUSES.indexOf(status) === -1) return draft;
      var area = ensureArea(draft, areaKey);
      if (status !== 'selected') {
        area.variant_ids.slice().forEach(function (vid) { removeItem(draft, areaKey, vid); });
        draft.areas[areaKey].status = status;
      } else {
        area.status = status;
      }
      return draft;
    }

    function setQuantity(draft, vid, qty) {
      if (draft.items[vid]) draft.items[vid].quantity = clampQty(qty);
      return draft;
    }

    /**
     * Drop any stored variant that no longer resolves to a live, purchasable
     * variant. Returns the list of removed variant IDs so the caller can flag the
     * affected areas for review.
     */
    function pruneUnavailable(draft, isValid) {
      var removed = [];
      Object.keys(draft.items).forEach(function (vid) {
        if (isValid(vid)) return;
        removed.push(vid);
        (draft.items[vid].area_keys || []).slice().forEach(function (k) { removeItem(draft, k, vid); });
        delete draft.items[vid];
      });
      return removed;
    }

    function matchesProfile(draft, profile) {
      var s = draft.profile_snapshot || {};
      var p = profile || {};
      return s.name === (p.name || '') &&
        s.size === (p.size || '') &&
        s.life_stage === (p.life_stage || '') &&
        (s.characteristics || []).join(',') === ((p.characteristics || []).join(','));
    }

    function selectedItems(draft) {
      return Object.keys(draft.items).map(function (vid) { return draft.items[vid]; });
    }

    /**
     * Is this a draft worth offering to resume outside the builder? Version- and
     * shape-valid (read() guarantees that), with a selected area AND real progress
     * inside it: a chosen variant, or an area decisively resolved as already
     * owned, skipped or saved for later. An empty shell is not "a pack waiting".
     */
    function isResumable(draft) {
      if (!draft || !draft.selected_areas || !draft.selected_areas.length) return false;
      if (Object.keys(draft.items).length > 0) return true;
      return Object.keys(draft.areas).some(function (k) {
        return DECISIVE.indexOf((draft.areas[k] || {}).status) !== -1;
      });
    }

    return {
      KEY: KEY, journey: JOURNEY, allowedAreas: ALLOWED,
      VERSION: VERSION, MAX_QTY: MAX_QTY, STATUSES: STATUSES,
      read: read, create: create, write: write, forget: forget,
      addItem: addItem, removeItem: removeItem, setStatus: setStatus, setQuantity: setQuantity,
      pruneUnavailable: pruneUnavailable, matchesProfile: matchesProfile, selectedItems: selectedItems,
      isResumable: isResumable
    };
  }

  // The default store is the everyday Build My Dog's Pack draft, unchanged — so
  // every existing caller of window.PackDraft.read()/write()/... keeps working.
  var defaultStore = createStore({ key: 'dog_owned_pack_v1' });
  defaultStore.createStore = createStore;
  return defaultStore;
})();
